import { useEffect, useMemo, useState, useCallback } from "react";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { getAllGrupos } from "../../../services/grupo.service";
import { getAllIdiomas } from "../../../services/idioma.service";
import { GrupoList } from "../../../modules/grupo/GrupoList";
import { TITULO_GRUPO_LIST } from "../../../utils/constantes";

export const GrupoPage = () => {
  const [idiomas, setIdiomas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [filtroIdioma, setFiltroIdioma] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const normalizeText = useCallback((text = "") => {
    return text
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }, []);

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      const [gruposRes, idiomasRes] = await Promise.all([
        getAllGrupos(),
        getAllIdiomas(),
      ]);
      
      console.log("Total de grupos recibidos:", gruposRes.length);
      
      if (gruposRes.length > 0) {
        console.log("Ejemplo de grupo:", {
          id: gruposRes[0].idGrupo,
          nombre: gruposRes[0].nombre,
          nombreAntGrupo: gruposRes[0].nombreAntGrupo || "No definido",
          modulo: gruposRes[0].modulo?.nombre || "Sin módulo"
        });
      }

      setGrupos(gruposRes);
      setIdiomas(idiomasRes);
      setError(null);
    } catch (err) {
      console.error("Error cargando datos:", err);
      setError("Error al cargar los grupos. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const gruposProcesados = useMemo(() => {
    return grupos.map(grupo => ({
      ...grupo,
      idIdioma: grupo.modulo?.idioma?.idIdioma || grupo.modulo?.idIdioma,
      searchString: `${grupo.nombre} ${grupo.nombreAntGrupo || ''} ${grupo.modulo?.nombre || ''} ${grupo.modulo?.sigla || ''}`
    }));
  }, [grupos]);

  const gruposFiltrados = useMemo(() => {
    if (!filtroIdioma && !busqueda) return gruposProcesados;

    const terminoBusqueda = busqueda ? normalizeText(busqueda) : null;

    return gruposProcesados.filter(grupo => {
      if (filtroIdioma && grupo.idIdioma !== filtroIdioma) return false;
      
      if (terminoBusqueda) {
        return normalizeText(grupo.searchString).includes(terminoBusqueda);
      }
      
      return true;
    });
  }, [gruposProcesados, filtroIdioma, busqueda, normalizeText]);

  if (loading) {
    return (
      <ContenedorPageTemplate titulo={TITULO_GRUPO_LIST}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-lg">Cargando grupos...</span>
        </div>
      </ContenedorPageTemplate>
    );
  }

  // Estado de error
  if (error) {
    return (
      <ContenedorPageTemplate titulo={TITULO_GRUPO_LIST}>
        <div className="flex flex-col items-center justify-center h-64 p-4 bg-red-50 rounded-lg">
          <p className="text-red-600 font-medium text-lg mb-3">{error}</p>
          <button 
            onClick={cargarDatos}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </ContenedorPageTemplate>
    );
  }

  return (
    <ContenedorPageTemplate titulo={TITULO_GRUPO_LIST}>
      <div className="flex flex-col sm:flex-row items-center gap-4 mb-6">
        <div className="relative w-full sm:w-auto">
          <select
            value={filtroIdioma}
            onChange={(e) => setFiltroIdioma(e.target.value)}
            className="w-full appearance-none bg-blue-600 text-white font-medium py-2 px-4 pr-8 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
          >
            <option value="" className="bg-white text-gray-800">
              Todos los idiomas
            </option>
            {idiomas.map(({ idIdioma, nombre }) => (
              <option
                key={idIdioma}
                value={idIdioma}
                className="bg-white text-gray-800"
              >
                {nombre}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        <input
          type="text"
          placeholder="Buscar por nombre, módulo, sigla…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="w-full flex-1 border border-gray-300 rounded-lg py-2 px-4 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
        />
      </div>

      <div className="bg-blue-50 rounded-lg p-4 mb-6">
        <p className="font-medium text-lg">
          Total de grupos: <span className="font-bold text-blue-700">{gruposFiltrados.length}</span>
        </p>
        <p className="text-sm text-gray-600 mt-1">
          {gruposFiltrados.length > 0 
            ? "Mostrando todos los grupos disponibles" 
            : "No se encontraron grupos con los filtros seleccionados"}
        </p>
      </div>

      <GrupoList grupos={gruposFiltrados} />
    </ContenedorPageTemplate>
  );
};